import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Home.css';
import '../styles/common.css';

import HeroSection from '../components/HeroSection';
import JourneySummary from '../components/JourneySummary';
import BlogHubCards from '../components/BlogHubCards';
import TechNotesSection from '../components/TechNotesSection';
import MemoryPreview from '../components/MemoryPreview';
import DevBlogPreview from '../components/DevBlogPreview';
import ExternalArchiveSection from '../components/ExternalArchiveSection';

function Home() {
  const { t } = useTranslation();
  const leadWords = t('manifesto.lead').split(' ');
  const bodyWords = t('manifesto.body').split(' ');
  const manifestoRef = useRef(null);
  const [manifestoVisible, setManifestoVisible] = useState(false);

  useEffect(() => {
    const el = manifestoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setManifestoVisible(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const heroGiant = document.querySelector('.hero-giant-bg');
    const handleScroll = () => {
      if (heroGiant) heroGiant.style.transform = `translateY(${window.scrollY * 0.25}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            const kids = entry.target.querySelectorAll('[data-stagger]');
            kids.forEach((el, i) => {
              el.style.transitionDelay = `${0.06 + i * 0.08}s`;
              el.classList.add('stagger-in');
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.home-section').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-skin">
      <main className="home-page">
        <HeroSection />

        <section className="manifesto-section" ref={manifestoRef}>
          <div className="manifesto-annotation">
            <div className="manifesto-kicker"><span>Essay</span></div>
            <div className="annotation-text">
              <p className="annotation-lead">
                {leadWords.map((w, i) => (
                  <span
                    key={i}
                    className={'word-reveal' + (manifestoVisible ? ' word-reveal--in' : '')}
                    style={{ transitionDelay: i * 0.06 + 's' }}
                  >{w}</span>
                ))}
              </p>
              <p className="annotation-body">
                {bodyWords.map((w, i) => (
                  <span
                    key={i}
                    className={'word-reveal' + (manifestoVisible ? ' word-reveal--in' : '')}
                    style={{ transitionDelay: (leadWords.length + i) * 0.04 + 's' }}
                  >{w}</span>
                ))}
              </p>
            </div>
            <div className="annotation-rule" />
          </div>
        </section>

        <div className="home-content-wrapper">
          <section className="home-section journey-section">
            <div className="section-container">
              <JourneySummary />
            </div>
          </section>

          <section className="home-section blog-section">
            <div className="section-container">
              <div className="section-header">
                <div className="section-header-left">
                  <span className="section-kicker">{t('home.insightKicker')}</span>
                  <h2 className="section-title">{t('home.insightTitle')}</h2>
                  <p className="section-desc">{t('home.insightDesc')}</p>
                </div>
              </div>
              <BlogHubCards />
              <div className="home-disclaimer">
{t('home.disclaimer')}
              </div>
            </div>
          </section>

          <section className="home-section devblog-home-section">
            <div className="section-container">
              <div className="section-header">
                <div className="section-header-left">
                  <span className="section-kicker">{t('home.devKicker')}</span>
                  <h2 className="section-title">{t('home.devTitle')}</h2>
                  <p className="section-desc">{t('home.devDesc')}</p>
                </div>
              </div>
              <DevBlogPreview />
            </div>
          </section>

          <section className="home-section gallery-section">
            <div className="section-container">
              <div className="section-header">
                <div className="section-header-left">
                  <span className="section-kicker">{t('home.archiveKicker')}</span>
                  <h2 className="section-title">{t('home.archiveTitle')}</h2>
                  <p className="section-desc">{t('home.archiveDesc')}</p>
                </div>
              </div>
              <MemoryPreview />
            </div>
          </section>

          <section className="home-section archive-section">
            <div className="section-container">
              <ExternalArchiveSection
                kicker={t('home.labsKicker')}
                showTitle
                title={t('home.labsTitle')}
                techTabLabel={t('home.techTab')}
                growthTabLabel={t('home.growthTab')}
              />
              <div className="archive-disclaimer">
{t('home.labsDisclaimer')}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default Home;
